import { useEffect, useMemo } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useAdSlotWithRefresh } from "~/components/advertisement/ad-slots/ad-slot/hooks";
import type { UniqueArticlesPerSectionItem } from "~/components/magazines-style/helpers";
import { useStoryStyleContext } from "~/components/story-style/contexts";
import { ArrowLeft, ArrowRight } from "~/components/story-style/story-style-arrow";
import { StoryStyleCard } from "~/components/story-style/story-style-card";
import { StoryStyleCloseButton } from "~/components/story-style/story-style-close-button";
import { StoryStyleNewsletterSignUpCard } from "~/components/story-style/story-style-newsletter-sign-up-card";
import { StoryStyleProgressBar } from "~/components/story-style/story-style-progress-bar";
import type { storyStyleLargeLayoutQueueItemsEdge$key } from "~/queries/__generated__/storyStyleLargeLayoutQueueItemsEdge.graphql";

import { getCurrentDisplayType } from "./helpers";
import {
  Container,
  ContentContainer,
  LHSContainer,
  LHSContent,
  LHSWrapper,
  RHSBannerAdSlotWrapper,
  Section,
  SliderWrapper,
  Slides,
  StyledAdSlot,
  StyledImgStyleStories,
} from "./styles";

type Props = {
  className?: string;
  layoutBreakpoint: string;
  reference: storyStyleLargeLayoutQueueItemsEdge$key;
  uniqueArticlesPerSection: UniqueArticlesPerSectionItem[];
};

export const StoryStyleLargeLayout: FunctionComponent<Props> = ({
  className,
  layoutBreakpoint,
  reference,
  uniqueArticlesPerSection,
}) => {
  const queueItemEdges = useFragment(
    graphql`
      fragment storyStyleLargeLayoutQueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Article {
            images(filter: { type: { exclude: null } }) {
              wide_landscape: style(filter: { style: "wide_landscape" }) {
                url
              }
            }
          }
        }
      }
    `,
    reference,
  );
  const {
    articleCardState,
    currentPageIndex,
    handleGoToNextPage,
    handleGoToPreviousPage,
    maximumCardIndex,
    totalArticleCount,
  } = useStoryStyleContext();

  const currentDisplayType = getCurrentDisplayType({
    currentIndex: currentPageIndex,
    maximumCardIndex,
  });

  const articleCardIndex = useMemo(
    () => articleCardState?.articleCardIndex ?? 0,
    [articleCardState?.articleCardIndex],
  );

  const currentArticleBackgroundImages = useMemo(() => {
    if (currentDisplayType !== "article") return;
    return [
      queueItemEdges?.[articleCardIndex]?.node?.images?.[0]?.wide_landscape?.url ?? "",
      // For preloading the next background image
      queueItemEdges?.[articleCardIndex + 1]?.node?.images?.[0]?.wide_landscape?.url ?? "",
    ];
  }, [articleCardIndex, currentDisplayType, queueItemEdges]);

  const handleRenderCard = () => {
    let currentIndex = 0;
    return uniqueArticlesPerSection.map(({ articleEdges, section }) =>
      articleEdges.map(article => (
        <StoryStyleCard
          hasBottomBannerAd={false}
          hasCoverLogo={currentIndex === 0}
          index={currentIndex}
          isHidden={currentDisplayType !== "article" || currentIndex++ !== articleCardIndex}
          key={article.node.entityId}
          reference={article.node}
          section={section.name}
        />
      )),
    );
  };

  const { props: adSlotProps, refresh: refreshAdSlot } = useAdSlotWithRefresh({
    adUnit: "d_oop3",
    breakpoint: layoutBreakpoint,
    sizes: [[300, 600], "fluid"],
    zone: "interstitial_style/card",
  });

  useEffect(() => {
    refreshAdSlot();
  }, [articleCardIndex, refreshAdSlot]);

  return (
    <Container $backgroundImages={currentArticleBackgroundImages} className={className}>
      <ContentContainer>
        <LHSContainer>
          <LHSWrapper>
            <StyledImgStyleStories />
            <LHSContent>Your quintessential guide to luxury reimagined.</LHSContent>
          </LHSWrapper>
        </LHSContainer>
        <SliderWrapper>
          <ArrowLeft disabled={currentPageIndex === 0} noStyle onClick={handleGoToPreviousPage} />
          <ArrowRight
            disabled={currentPageIndex === maximumCardIndex}
            noStyle
            onClick={handleGoToNextPage}
          />
          <Slides>
            <Section>
              {currentDisplayType === "newsletter-sign-up" && (
                <>
                  <StoryStyleNewsletterSignUpCard
                    handleGoToNextPage={handleGoToNextPage}
                    handleGoToPreviousPage={handleGoToPreviousPage}
                  />
                  <StoryStyleCloseButton />
                </>
              )}
              {currentDisplayType === "article" && (
                <>
                  <StoryStyleProgressBar
                    articleCardIndex={articleCardIndex}
                    totalCardLength={totalArticleCount}
                  />
                  <StoryStyleCloseButton />
                </>
              )}
              {handleRenderCard()}
            </Section>
          </Slides>
        </SliderWrapper>
        <RHSBannerAdSlotWrapper>
          <StyledAdSlot {...adSlotProps} />
        </RHSBannerAdSlotWrapper>
      </ContentContainer>
    </Container>
  );
};

StoryStyleLargeLayout.displayName = "StoryStyleLargeLayout";
